import { mapState } from 'vuex';
import FieldValidator from '@/views/calculators/services/fieldValidatorService';
import { AccidentSettings } from '../AccidentSettings';
import ExistingAbProductsComponent from '../../components/ExistingABProductsComponent.vue';
import { Options, Vue } from 'vue-class-component';
import { nextTick, PropType } from 'vue';
import { CardBlock } from '@/cms/definitions/content-types';
import store from '@/store/store';
import { Validator } from '../../services/ValidationService';
import { modalWithExit, togglePopup } from '../../services/AlertService';
import { CustomerCalculation } from '../../types/types';
import { handleZipCodePrefill } from '../../services/UiStepService';
import { handleDawaAddressSearch } from '@/views/calculators/services/addressMappingService';

@Options({
	name: 'PersonInfoStepComponent',
	props: {
		card: Object as PropType<CardBlock>,
		isValid: Boolean,
	},
	components: {
		ExistingAbProductsComponent,
	},
	computed: mapState<any>({
		model: (state) => state.calculatorContext[state.calculatorContext.active].model,
		cms: (state) => state.calculatorContext[state.calculatorContext.active].cms,
	}),
})
export default class PersonInfoStepComponent extends Vue {
	card: CardBlock; // Card from cms
	isValid: boolean; // whether card is complete

	public model!: any; // data structure for user input (store)
	public cms!: AccidentSettings; // settings from CMS (store)

	public fieldValidator: FieldValidator = Validator;
	public defaultZip: string = null;
	public readonlyAge: string = null;
	public otherPerson = false;
	public defaultAddress: string = null;

	public created() {
		const zipParam = (this.$route.query.zipCode as string) || (this.$route.query.zipcode as string);
		if (zipParam) {
			this.defaultZip = zipParam;
		}

		const addressParam = this.$route.query.address as string;
		if (addressParam) {
			this.defaultAddress = addressParam;
		}
	}

	public mounted() {
		const customerCalculation: CustomerCalculation = store.getters.getCustomerCalculation;
		handleZipCodePrefill(this.model.personInfo, this.cms);

		addEventListener('keyup', this.moveOn);

		if (this.model.personInfo.otherName) {
			this.otherPerson = true;
			this.model.personInfo.isMainPerson = false;
			return;
		}

		if (this.model.multipleProducts) {
			const commonModel = store.getters.getCalculatorCommonModel;
			if (this.fieldValidator.isValidAge(commonModel.personInfo.customerAge)) {
				this.readonlyAge = commonModel.personInfo.customerAge;
			}

			if (!this.model.personInfo.isMainPerson) {
				const basket = store.getters.getSelectableGroup('basket');
				const activeIndex = store.getters.getActiveCalculatorInx;

				let mainPerson = true;
				basket.forEach((product) => {
					if (
						mainPerson &&
						product.startsWith(this.model.productName) &&
						product !== this.model.productName + activeIndex
					) {
						const calc = store.getters.getCalculator(product);
						if (calc?.model?.personInfo?.isMainPerson) {
							mainPerson = false;
						}
					}
				});

				if (mainPerson) {
					this.otherPerson = false;
					this.model.personInfo.isMainPerson = true;
					this.model.personInfo.otherName = undefined;
					this.model.personInfo.otherCustomerAge = undefined;
					this.model.personInfo.otherCpr = undefined;
				} else {
					this.otherPerson = true;
					this.model.personInfo.isMainPerson = false;
				}
			}
		} else {
			this.otherPerson = false;
			this.model.personInfo.isMainPerson = true;
			this.model.personInfo.otherName = undefined;
			this.model.personInfo.otherCustomerAge = undefined;
			this.model.personInfo.otherCpr = undefined;

			if (
				!this.readonlyAge &&
				customerCalculation.hasCustomerData &&
				this.fieldValidator.isValidAge(customerCalculation?.customer?.personInfo?.customerAge)
			) {
				this.readonlyAge = customerCalculation.customer.personInfo.customerAge;
			}
		}
	}

	public beforeUnmount() {
		removeEventListener('keyup', this.moveOn);
	}

	public moveOn(evt) {
		if (evt.keyCode === 13 && this.isValid) {
			this.nextStep();
		}
	}

	public async addressSearch(address) {
		if (await handleDawaAddressSearch(this.model.personInfo, this.model.personInfo.homeAddress, address)) {
			this.model.pristineStep.set(this.card.name, true);
			nextTick(() => {
				// wait for transition to begin showing customerAge field
				setTimeout(() => {
					const input = document.getElementById('customerAge');
					if (input) {
						input.focus();
					}
				}, 300);
			});
			return;
		}
	}

	public nextStep() {
		this.$emit('nextStep', this.card.routePath);
	}

	public workSelected(result) {
		if (result.matchText) {
			this.model.personInfo.work = result.matchText;
			const inx = this.cms.work.indexOf(result.matchText);
			this.model.personInfo.workId = this.cms.workIds[inx];
			return;
		}
		this.model.personInfo.work = undefined;
		this.model.personInfo.workId = undefined;
	}

	public get workLabel(): string {
		if (this.otherPerson && this.model.personInfo.otherName) {
			const name = this.model.personInfo.otherName.trim().split(' ');
			if (name[0].length > 1) {
				return `Hvad arbejder ${name[0]} med?`;
			}
		}
		return 'Hvad arbejder person med?';
	}
	public workSearchError() {
		const modal = modalWithExit(this.cms);
		modal.title = 'Problem med at finde dit job';
		modal.content =
			'Der er desværre i øjeblikket problemer med jobsøgning. Prøv igen om et øjeblik eller kontakt os.';
		modal.id = 'workSearchError';
		togglePopup(this.cms, modal);
	}
}
